/* Portal hooks: every path is /api/portal/*, never an entity id (m6-rulings.md #122, #125, #132).
   Mirrors the console hooks one-for-one; the session's pinned company scopes every call. */

import { useEffect, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type Query,
} from "@tanstack/react-query";
import { ApiError, apiBlob, apiGet, apiSend, apiText } from "./client";
import type {
  ConnectionMethod,
  DocumentDetail,
  DocumentEvent,
  DocumentSummary,
  EntityStatus,
  Health,
  OutcomeLegs,
  Page,
  Role,
  StageCounts,
  ValidationResult,
} from "./types";

export const FAST_POLL = 5000;
export const SLOW_POLL = 30000;

export type PortalEntity = {
  id: string;
  code: string;
  legalName: string;
  crNumber: string;
  participantId: string;
  connectionMethod: ConnectionMethod;
  status: EntityStatus;
  health: Health;
};

export type PortalMe = {
  user: { id: string; email: string; displayName: string };
  role: Role;
  entity: PortalEntity;
  surface: "portal";
  helpdeskEmail: string | null;
};

export type PortalSummary = {
  sentToday: number;
  inFlight: number;
  needsAttention: number;
  lastDocumentAt: string | null;
  stageCountsToday: StageCounts;
  stageCountsMonth: StageCounts;
  connection: { method: ConnectionMethod; health: Health; lastContactAt: string | null };
};

/** Pinned shape (m6-rulings.md #119); hex lowercase, nulls where absent. */
export type PortalArchive = {
  xmlSha256: string;
  aspAckSha256: string | null;
  qrSha256: string | null;
  peppolMlsRef: string | null;
  otaRef: string | null;
  sealedAt: string;
  sealVersion: number;
  artifacts: { kind: string; sha256: string; bytes: number }[];
};

/** The row may carry the server's message for an error; the console row type does not promise it. */
export type PortalDocumentRow = DocumentSummary & { errorMessage?: string | null };

/** The console filters minus the company: the session pins it. */
export type PortalDocumentFilters = {
  direction?: string;
  stage?: string;
  state?: string;
  q?: string;
  from?: string;
  to?: string;
  family?: string;
  typeCode?: string;
  limit?: number;
};

const FILTER_KEYS = ["direction", "stage", "state", "q", "from", "to", "family", "typeCode", "limit"] as const;

export function portalDocumentQuery(filters: PortalDocumentFilters, cursor?: string | null): string {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  if (cursor) params.set("cursor", cursor);
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function usePortalMe() {
  return useQuery({ queryKey: ["portal", "me"], queryFn: () => apiGet<PortalMe>("/api/portal/me") });
}

export function usePortalSummary() {
  return useQuery({
    queryKey: ["portal", "summary"],
    queryFn: () => apiGet<PortalSummary>("/api/portal/summary"),
    refetchInterval: SLOW_POLL,
  });
}

/** The queue polls fast; the overview passes `SLOW_POLL` (brief: 30 s); `false` pauses. */
export function usePortalDocuments(filters: PortalDocumentFilters = {}, interval: number | false = FAST_POLL) {
  return useInfiniteQuery({
    queryKey: ["portal", "documents", "list", filters],
    queryFn: ({ pageParam }) =>
      apiGet<Page<PortalDocumentRow>>(`/api/portal/documents${portalDocumentQuery(filters, pageParam)}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: interval,
  });
}

export function usePortalStageCounts(filters: Pick<PortalDocumentFilters, "direction"> = {}, paused = false) {
  return useQuery({
    queryKey: ["portal", "documents", "stage-counts", filters],
    queryFn: () => apiGet<StageCounts>(`/api/portal/documents/stage-counts${portalDocumentQuery(filters)}`),
    refetchInterval: paused ? false : FAST_POLL,
  });
}

export function usePortalDocument(docId: string | undefined) {
  return useQuery({
    queryKey: ["portal", "documents", docId],
    queryFn: () => apiGet<DocumentDetail>(`/api/portal/documents/${docId}`),
    enabled: Boolean(docId),
    // Another company's document is 404 by design; retrying it buys nothing.
    retry: (count, error) => count < 1 && !(error instanceof ApiError && error.status === 404),
    // A document only moves while it is in flight; archived and terminal stop the poll.
    refetchInterval: (query: Query<DocumentDetail>) =>
      query.state.data?.state === "in_flight" ? FAST_POLL : false,
  });
}

export function usePortalDocumentEvents(docId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["portal", "documents", docId, "events"],
    queryFn: async () =>
      (await apiGet<{ items: DocumentEvent[] }>(`/api/portal/documents/${docId}/events`)).items,
    enabled: Boolean(docId) && enabled,
  });
}

export function usePortalDocumentValidation(docId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["portal", "documents", docId, "validation"],
    queryFn: () => apiGet<ValidationResult>(`/api/portal/documents/${docId}/validation`),
    enabled: Boolean(docId) && enabled,
  });
}

export function usePortalDocumentLegs(docId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["portal", "documents", docId, "legs"],
    queryFn: () => apiGet<OutcomeLegs>(`/api/portal/documents/${docId}/legs`),
    enabled: Boolean(docId) && enabled,
    refetchInterval: (query: Query<OutcomeLegs>) => {
      const legs = query.state.data;
      if (!legs) return FAST_POLL;
      const pending = [legs.aspAck, legs.peppolMls, legs.otaRef].some(
        (leg) => !leg || leg.status === "pending",
      );
      return pending ? FAST_POLL : false;
    },
  });
}

export function usePortalDocumentXml(docId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["portal", "documents", docId, "xml"],
    queryFn: () => apiText(`/api/portal/documents/${docId}/xml`, "application/xml"),
    enabled: Boolean(docId) && enabled,
  });
}

export function usePortalDocumentArchive(docId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["portal", "documents", docId, "archive"],
    queryFn: () => apiGet<PortalArchive>(`/api/portal/documents/${docId}/archive`),
    // Asking before the seal answers 404 on a perfectly normal page.
    enabled: Boolean(docId) && enabled,
    retry: false,
  });
}

export type QrState = { url: string | null; isLoading: boolean; error: Error | null };

/** Object URL rather than a query: the blob has to be revoked on unmount. */
export function usePortalDocumentQr(docId: string | undefined): QrState {
  const [state, setState] = useState<QrState>({ url: null, isLoading: true, error: null });

  useEffect(() => {
    if (!docId) {
      setState({ url: null, isLoading: false, error: null });
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    setState({ url: null, isLoading: true, error: null });

    apiBlob(`/api/portal/documents/${docId}/qr.png`)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ url: objectUrl, isLoading: false, error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const missing = error instanceof ApiError && error.status === 404;
        setState({ url: null, isLoading: false, error: missing ? null : (error as Error) });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [docId]);

  return state;
}

type ListData = InfiniteData<Page<PortalDocumentRow>>;
type Snapshot = [readonly unknown[], unknown][];

/** Only an outbound document restarts at the first stage; an inbound one is the central team's and is left alone. */
function restarted<T extends PortalDocumentRow | DocumentDetail>(row: T): T {
  if (row.direction !== "out") return row;
  return { ...row, stage: "erp_detected", state: "in_flight", errorClass: null, errorCode: null, errorMessage: null };
}

/** Optimistic: cached lists and the detail move to the first stage at once, and roll back on failure. */
export function usePortalReprocess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) => apiSend<PortalDocumentRow>("POST", `/api/portal/documents/${docId}/reprocess`),
    onMutate: async (docId) => {
      await queryClient.cancelQueries({ queryKey: ["portal", "documents"] });
      const previous: Snapshot = queryClient.getQueriesData({ queryKey: ["portal", "documents"] });
      queryClient.setQueriesData<ListData>({ queryKey: ["portal", "documents", "list"] }, (data) =>
        data
          ? {
              ...data,
              pages: data.pages.map((page) => ({
                ...page,
                items: page.items.map((row) => (row.id === docId ? restarted(row) : row)),
              })),
            }
          : data,
      );
      queryClient.setQueryData<DocumentDetail>(["portal", "documents", docId], (doc) =>
        doc ? restarted(doc) : doc,
      );
      return { previous };
    },
    onError: (_error, _docId, context) => {
      for (const [key, data] of context?.previous ?? []) queryClient.setQueryData(key, data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["portal", "documents"] });
      queryClient.invalidateQueries({ queryKey: ["portal", "summary"] });
    },
  });
}
